import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import VehicleDetail from './components/VehicleDetail';
import { useSiteData } from '@/data/siteData';
import { applySeo } from '@/lib/seo';

export default function VehiclePage() {
  const { id } = useParams();
  const { vehicles, loading } = useSiteData();
  const vehicle = vehicles.find((v) => v.id === id);

  useEffect(() => {
    if (loading) return;

    if (vehicle) {
      applySeo({
        title: `${vehicle.brand} ${vehicle.model} ${vehicle.motorisation} d'occasion | Easy Car`,
        description: `${vehicle.brand} ${vehicle.model} ${vehicle.motorisation}, ${vehicle.year}, ${vehicle.mileage.toLocaleString('fr-FR')} km. Véhicule d'occasion disponible chez Easy Car à ${vehicle.location}, La Réunion.`,
        canonical: `https://easycar.re/vehicule/${vehicle.id}`,
      });
    } else {
      applySeo({
        title: 'Véhicule introuvable | Easy Car',
        description: "Ce véhicule n'est plus disponible sur Easy Car.",
        robots: 'noindex,nofollow',
      });
    }

    window.scrollTo(0, 0);
  }, [vehicle, loading]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background-50">
        <Header />
        <main className="pt-28 md:pt-32 pb-16">
          <div className="w-full px-4 md:px-6 lg:px-10 text-center text-foreground-500">Chargement du véhicule…</div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div className="min-h-screen bg-background-50">
        <Header />
        <main className="pt-28 md:pt-32 pb-16">
          <div className="w-full px-4 md:px-6 lg:px-10 max-w-2xl mx-auto text-center">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              Véhicule introuvable
            </h1>
            <p className="text-foreground-600 text-sm mb-6">
              Le véhicule que vous recherchez n'existe pas ou n'est plus disponible.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-white font-label font-semibold text-sm hover:bg-primary-600 transition-all whitespace-nowrap"
            >
              <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center" />
              Retour à l'accueil
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Header />
      <main className="pt-20 md:pt-24 pb-16">
        <div className="w-full px-4 md:px-6 lg:px-10">
          <div className="flex items-center justify-between gap-2 text-sm text-foreground-500 mb-4 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">
                Accueil
              </Link>
              <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center" />
              <span className="text-foreground-900 font-medium whitespace-nowrap">
                {vehicle.brand} {vehicle.model}
              </span>
            </div>
            <Link
              to="/vehicules"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-foreground-300 text-foreground-700 font-label font-medium text-sm hover:border-primary-500 hover:text-primary-500 transition-all whitespace-nowrap"
            >
              <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center" />
              Voir tous nos véhicules
            </Link>
          </div>
        </div>

        <VehicleDetail vehicle={vehicle} />
      </main>
      <Footer />
    </div>
  );
}
